import type { Profile } from "../types";
import { getProfileAuthApiUrl } from "./profile-auth";
import { sanitizeProfilesForPersistence } from "./profile-sanitize";

export interface RemoteProfileStore {
	profiles: Profile[];
	activeProfileId: string | null;
}

interface RawRemoteProfileStore {
	profiles?: Profile[];
	activeProfileId?: string | null;
	message?: string;
}

const PROFILE_AUTH_ERROR_MESSAGES = new Set([
	"Missing access token",
	"Invalid or expired token",
	"User not found",
]);

function endpoint(apiUrl?: string): string {
	return `${getProfileAuthApiUrl(apiUrl).replace(/\/$/, "")}/api/sdk/v1/profiles`;
}

export function hasUsableProfileAccessToken(accessToken: unknown): accessToken is string {
	if (typeof accessToken !== "string") return false;
	const token = accessToken.trim();
	return token.length > 0 && token.split(".").length === 3;
}

export function isProfileAuthError(error: unknown): boolean {
	const message = error instanceof Error
		? error.message
		: typeof error === "string"
			? error
			: "";
	if (PROFILE_AUTH_ERROR_MESSAGES.has(message)) return true;
	return /access token|expired token|unauthorized|not authenticated/i.test(message);
}

function requireProfileAccessToken(accessToken: unknown): string {
	if (!hasUsableProfileAccessToken(accessToken)) {
		throw new Error("Missing access token");
	}
	return accessToken.trim();
}

function normalizeStore(data: RawRemoteProfileStore): RemoteProfileStore {
	return {
		profiles: Array.isArray(data.profiles) ? sanitizeProfilesForPersistence(data.profiles) : [],
		activeProfileId: data.activeProfileId ?? null,
	};
}

async function parseResponse(response: Response): Promise<RawRemoteProfileStore> {
	const data = await response.json().catch(() => ({}));
	if (!response.ok) {
		const message = typeof data.message === "string" ? data.message : "Profile sync failed.";
		throw new Error(message);
	}
	return data as RawRemoteProfileStore;
}

export async function fetchRemoteProfiles(apiUrl: string | undefined, accessToken: string): Promise<RemoteProfileStore> {
	const token = requireProfileAccessToken(accessToken);
	const response = await fetch(endpoint(apiUrl), {
		method: "GET",
		headers: { Authorization: `Bearer ${token}` },
		cache: "no-store",
	});
	return normalizeStore(await parseResponse(response));
}

export async function saveRemoteProfiles(
	apiUrl: string | undefined,
	accessToken: string,
	profiles: Profile[],
	activeProfileId: string | null,
): Promise<RemoteProfileStore> {
	const token = requireProfileAccessToken(accessToken);
	const response = await fetch(endpoint(apiUrl), {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		cache: "no-store",
		body: JSON.stringify({ profiles: sanitizeProfilesForPersistence(profiles), activeProfileId }),
	});
	return normalizeStore(await parseResponse(response));
}

export async function deleteRemoteProfile(
	apiUrl: string | undefined,
	accessToken: string,
	profileId: string,
): Promise<RemoteProfileStore> {
	const token = requireProfileAccessToken(accessToken);
	const response = await fetch(`${endpoint(apiUrl)}/${encodeURIComponent(profileId)}`, {
		method: "DELETE",
		headers: { Authorization: `Bearer ${token}` },
		cache: "no-store",
	});
	return normalizeStore(await parseResponse(response));
}
